import React, { useState, useEffect } from "react";
import {
  X,
  Mail,
  Clock,
  AlertTriangle,
  Info,
  Globe,
  ChevronDown,
} from "lucide-react";
import {
  MonitoringFrequency,
  WeekDay,
  CreateMonitoringRequest,
} from "../../services/EmailHygieneService";
import {
  validateMonitoringForm,
  MonitoringFormErrors,
} from "./MonitoringFormValidation";

interface AddMonitoringModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateMonitoringRequest) => Promise<void>;
  initialDomain?: string;
}

const AddMonitoringModal: React.FC<AddMonitoringModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialDomain,
}) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<CreateMonitoringRequest>({
    userEmail: "",
    parameterEmail: initialDomain || "",
    frequency: MonitoringFrequency.Daily,
    preferredTime: "09:00",
    preferredDay: WeekDay.Monday,
    preferredDate: 1,
  });

  const [errors, setErrors] = useState<MonitoringFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetModal = () => {
    setStep(1);
    setFormData({
      userEmail: "",
      parameterEmail: initialDomain || "",
      frequency: MonitoringFrequency.Daily,
      preferredTime: "09:00",
      preferredDay: WeekDay.Monday,
      preferredDate: 1,
    });
    setErrors({});
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (isOpen) {
      resetModal();
    }
  }, [isOpen, initialDomain]);

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (step !== 2) return;

    const validationErrors = validateMonitoringForm(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setIsSubmitting(true);
      try {
        await onSubmit(formData);
        onClose();
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const validateStep1 = () => {
    const newErrors: MonitoringFormErrors = {};

    if (!formData.userEmail) {
      newErrors.userEmail = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.userEmail)) {
      newErrors.userEmail = "Please enter a valid email address";
    }

    if (!formData.parameterEmail) {
      newErrors.parameterEmail = "Domain is required";
    } else if (
      !/^(?!:\/\/)([a-zA-Z0-9-_]+\.)*[a-zA-Z0-9][a-zA-Z0-9-_]+\.[a-zA-Z]{2,11}?$/.test(
        formData.parameterEmail
      )
    ) {
      newErrors.parameterEmail =
        "Please enter a valid domain (e.g., example.com)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = (e: React.MouseEvent) => {
    e.preventDefault();
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData({ ...formData, userEmail: value });

    if (errors.userEmail) {
      setErrors({ ...errors, userEmail: undefined });
    }
  };

  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData({ ...formData, parameterEmail: value });

    if (errors.parameterEmail) {
      setErrors({ ...errors, parameterEmail: undefined });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-background-paper dark:bg-background-paperDark rounded-xl max-w-2xl w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-divider dark:border-divider-dark">
          <div>
            <h2 className="text-xl font-semibold">
              Configure Email Monitoring
            </h2>
            <p className="text-sm text-text-secondary mt-1">
              Step {step} of 2:{" "}
              {step === 1 ? "Basic Details" : "Schedule Settings"}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-background-panel dark:hover:bg-background-panelDark rounded-lg text-text-secondary hover:text-text-primary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="w-full h-1 bg-background-panel dark:bg-background-panelDark">
          <div
            className="h-full bg-yellow-DEFAULT transition-all duration-300"
            style={{ width: `${(step / 2) * 100}%` }}
          />
        </div>

        <form onSubmit={handleSubmit}>
          {step === 1 ? (
            <div className="p-6">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Notification Email
                    <span className="text-red-dark ml-1">*</span>
                  </label>
                  <div className="relative group">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-primary dark:text-text-primaryDark pointer-events-none" />
                    <input
                      type="email"
                      value={formData.userEmail}
                      onChange={handleEmailChange}
                      onBlur={() => validateStep1()}
                      className="w-full pl-10 pr-4 py-3 rounded-lg border bg-white dark:bg-background-paperDark text-text-primary dark:text-text-primaryDark placeholder:text-text-secondary dark:placeholder:text-text-secondaryDark border-divider dark:border-divider-dark focus:border-yellow-DEFAULT focus:ring-1 focus:ring-yellow-DEFAULT/20 transition-all duration-200 cursor-text"
                      placeholder="your@email.com"
                    />
                  </div>
                  {errors.userEmail && (
                    <div className="mt-1 text-sm text-red-dark flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />
                      {errors.userEmail}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Domain to Monitor
                    <span className="text-red-dark ml-1">*</span>
                  </label>
                  <div className="relative group">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-primary dark:text-text-primaryDark pointer-events-none" />
                    <input
                      type="text"
                      value={formData.parameterEmail}
                      onChange={handleDomainChange}
                      onBlur={() => validateStep1()}
                      className="w-full pl-10 pr-4 py-3 rounded-lg border bg-white dark:bg-background-paperDark text-text-primary dark:text-text-primaryDark placeholder:text-text-secondary dark:placeholder:text-text-secondaryDark border-divider dark:border-divider-dark focus:border-yellow-DEFAULT focus:ring-1 focus:ring-yellow-DEFAULT/20 transition-all duration-200 cursor-text"
                      placeholder="example.com"
                    />
                  </div>
                  {errors.parameterEmail && (
                    <div className="mt-1 text-sm text-red-dark flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />
                      {errors.parameterEmail}
                    </div>
                  )}
                </div>

                <div className="bg-background-panel dark:bg-background-panelDark rounded-lg p-4 flex items-start gap-3">
                  <Info className="w-5 h-5 text-yellow-DEFAULT shrink-0 mt-0.5" />
                  <p className="text-sm text-text-secondary">
                    We'll send monitoring results and alerts to your
                    notification email. Make sure it's an email you check
                    regularly.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Check Frequency
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {Object.values(MonitoringFrequency).map((freq) => (
                      <button
                        key={freq}
                        type="button"
                        onClick={() =>
                          setFormData({ ...formData, frequency: freq })
                        }
                        className={`p-3 rounded-lg border ${
                          formData.frequency === freq
                            ? "border-yellow-DEFAULT bg-yellow-DEFAULT/5"
                            : "border-divider dark:border-divider-dark hover:border-yellow-DEFAULT/50"
                        } transition-colors`}
                      >
                        <div className="text-sm font-medium">
                          {freq.charAt(0).toUpperCase() + freq.slice(1)}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Preferred Time
                  </label>
                  <div className="relative group">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-primary dark:text-text-primaryDark pointer-events-none" />
                    <select
                      value={formData.preferredTime}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          preferredTime: e.target.value,
                        })
                      }
                      className="w-full pl-10 pr-4 py-3 rounded-lg border bg-white dark:bg-background-paperDark text-text-primary dark:text-text-primaryDark border-divider dark:border-divider-dark focus:border-yellow-DEFAULT focus:ring-1 focus:ring-yellow-DEFAULT/20 transition-all duration-200 appearance-none cursor-pointer"
                    >
                      {Array.from({ length: 24 }, (_, hour) =>
                        ["00", "30"].map((minute) => {
                          const time = `${hour
                            .toString()
                            .padStart(2, "0")}:${minute}`;
                          return (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          );
                        })
                      )}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary pointer-events-none" />
                  </div>
                </div>

                {formData.frequency === MonitoringFrequency.Weekly && (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Preferred Day
                    </label>
                    <div className="grid grid-cols-7 gap-2">
                      {Object.entries(WeekDay)
                        .filter(([key]) => isNaN(Number(key)))
                        .map(([key, value]) => {
                          const isSelected = formData.preferredDay === value;
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  preferredDay: value as WeekDay,
                                })
                              }
                              className={`p-2 rounded-lg text-sm font-medium transition-colors ${
                                isSelected
                                  ? "bg-yellow-DEFAULT text-black dark:text-white ring-2 ring-yellow-DEFAULT/20"
                                  : "bg-background-paper dark:bg-background-paperDark hover:bg-background-panel dark:hover:bg-background-panelDark text-text-primary dark:text-text-primaryDark"
                              }`}
                            >
                              {key.slice(0, 3)}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                )}

                {formData.frequency === MonitoringFrequency.Monthly && (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Preferred Date
                    </label>
                    <div className="grid grid-cols-7 gap-2">
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(
                        (date) => {
                          const isSelected = formData.preferredDate === date;
                          return (
                            <button
                              key={date}
                              type="button"
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  preferredDate: date,
                                })
                              }
                              className={`p-2 rounded-lg text-sm font-medium transition-colors ${
                                isSelected
                                  ? "bg-yellow-DEFAULT text-black dark:text-white ring-2 ring-yellow-DEFAULT/20"
                                  : "bg-background-paper dark:bg-background-paperDark hover:bg-background-panel dark:hover:bg-background-panelDark text-text-primary dark:text-text-primaryDark"
                              }`}
                            >
                              {date}
                            </button>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-between px-6 py-4 border-t border-divider dark:border-divider-dark">
            {step === 2 ? (
              <>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-yellow-DEFAULT text-black dark:text-white rounded-lg hover:bg-yellow-DEFAULT/90 transition-colors disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Setting up..." : "Start Monitoring"}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleNextStep}
                  className="px-6 py-2 bg-yellow-DEFAULT text-black dark:text-white rounded-lg hover:bg-yellow-DEFAULT/90 transition-colors"
                >
                  Next
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMonitoringModal;
